import Ionicons from "@expo/vector-icons/Ionicons";
import {
  DateTimePickerAndroid,
  type DateTimePickerChangeEvent,
} from "@react-native-community/datetimepicker";
import { createElement, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { IOSPickerSheet } from "@/components/ui/ios-picker-sheet";

/** Local date ("YYYY-MM-DD") → Date at local midnight, or null if malformed. */
export function parseLocalDate(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function toLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "12 Mar 2024" — always with the year, since these reach back years. */
export function formatHistoryDate(value: string): string {
  const d = parseLocalDate(value);
  if (!d) return value;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * A single date, as a chip that opens the platform's picker: an Android
 * dialog, an inline iOS spinner, or a native `<input type="date">` on web.
 *
 * `value` is a local "YYYY-MM-DD" string or null. Unlike the goal due-date
 * picker this one looks backwards — it takes an open `minimumDate` so someone
 * can reach a reflection from years ago.
 */
export function DateField({
  value,
  onChange,
  placeholder,
  accessibilityLabel,
  minimumDate,
  maximumDate,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder: string;
  accessibilityLabel: string;
  minimumDate?: Date;
  maximumDate?: Date;
}) {
  const c = useTheme();
  const [showIOS, setShowIOS] = useState(false);
  const selected = parseLocalDate(value) ?? maximumDate ?? new Date();

  function onNativeValueChange(
    _event: DateTimePickerChangeEvent,
    next: Date,
  ) {
    onChange(toLocalDate(next));
  }

  function openPicker() {
    if (Platform.OS === "android") {
      DateTimePickerAndroid.open({
        value: selected,
        mode: "date",
        display: "default",
        minimumDate,
        maximumDate,
        onValueChange: onNativeValueChange,
        onDismiss: () => {},
      });
      return;
    }
    setShowIOS(true);
  }

  return (
    <View style={styles.root}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={Platform.OS === "web" ? undefined : openPicker}
        style={[
          styles.chip,
          {
            borderColor: value ? c.tint : c.border,
            backgroundColor: value ? c.backgroundSelected : c.surfaceAlt,
          },
        ]}
      >
        <Ionicons
          name="calendar-outline"
          size={15}
          color={value ? c.tint : c.textMuted}
        />
        {Platform.OS === "web" ? (
          createElement("input", {
            type: "date",
            value: value ?? "",
            min: minimumDate ? toLocalDate(minimumDate) : undefined,
            max: maximumDate ? toLocalDate(maximumDate) : undefined,
            "aria-label": accessibilityLabel,
            onChange: (e: { currentTarget: { value: string } }) =>
              onChange(e.currentTarget.value || null),
            style: StyleSheet.flatten([styles.webInput, { color: c.text }]),
          })
        ) : (
          <ThemedText variant="caption" tone={value ? "tint" : "secondary"}>
            {value ? formatHistoryDate(value) : placeholder}
          </ThemedText>
        )}
        {value && Platform.OS !== "web" ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`${accessibilityLabel} — clear`}
            onPress={() => onChange(null)}
            hitSlop={8}
          >
            <Ionicons name="close-circle" size={16} color={c.textMuted} />
          </Pressable>
        ) : null}
      </Pressable>

      {/* Same sheet the date-of-birth field uses, and for the same reason: an
          inline iOS wheel has no Done and no dismissal, so it stayed on screen
          until the chip was tapped a second time. */}
      {Platform.OS === "ios" ? (
        <IOSPickerSheet
          visible={showIOS}
          value={selected}
          onValueChange={onNativeValueChange}
          onClose={() => setShowIOS(false)}
          minimumDate={minimumDate}
          maximumDate={maximumDate}
          accessibilityLabel={accessibilityLabel}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: { gap: Spacing.xs },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: Spacing.md,
    paddingVertical: 6,
    borderRadius: Radius.md,
    borderWidth: 1,
  },
  webInput: {
    borderWidth: 0,
    backgroundColor: "transparent",
    fontSize: 13,
  },
});
